import { duplicationsOf } from "../utils/duplicationUtils";
import SourceRow from "./SourceRow";
import { Box, TextField, Typography } from "@mui/material";
import { getDuplicateSourcePrefix } from "config";
import * as labels from "config.labels";
import React, { useCallback, useMemo, useState } from "react";
import * as staticDataHooks from "store/hooks/staticDataHooks";

const SectionDivider = ({ label }) => (
  <Box
    sx={{
      textAlign: "center",
      py: 0.5,
      px: 1,
      backgroundColor: "grey.50",
      borderBottom: "1px solid",
      borderColor: "divider",
      position: "sticky",
      top: 0,
      zIndex: 1,
    }}
  >
    <Typography
      variant={"caption"}
      color={"text.secondary"}
      sx={{ fontWeight: 600, letterSpacing: 0.5 }}
    >
      {label}
    </Typography>
  </Box>
);

const displayNameOf = (source) => source.displayName || source.internalName;

// A parent passes the search when it matches, or when any of its duplicates
// does, so a duplicate is never shown without the row it hangs under.
const matchesSearch = (source, lower, duplications) =>
  (source.displayName || "").toLowerCase().includes(lower) ||
  source.internalName.toLowerCase().includes(lower) ||
  duplicationsOf(source.internalName, duplications).some((d) =>
    (d.displayName || "").toLowerCase().includes(lower)
  );

const TableSelector = ({
  tables,
  savedReports = [],
  selectedTables,
  onSelectedTablesChange,
  selectedReports = [],
  onSelectedReportsChange,
  onPreviewTable,
  onPreviewReport,
  onPreviewDuplication,
  onEditDuplication,
  onDuplicate,
  duplications = [],
  sortAlpha = true,
}) => {
  const translateLabels = staticDataHooks.useTranslator("uiLabels");
  const selectedSet = useMemo(() => new Set(selectedTables), [selectedTables]);
  const selectedReportSet = useMemo(() => new Set(selectedReports), [
    selectedReports,
  ]);
  const [filterText, setFilterText] = useState("");

  const filteredTables = useMemo(() => {
    const sorted = [...tables].sort((a, b) =>
      displayNameOf(a).localeCompare(displayNameOf(b))
    );
    if (!filterText.trim()) return sorted;
    const lower = filterText.toLowerCase();
    return sorted.filter((t) => matchesSearch(t, lower, duplications));
  }, [tables, filterText, duplications]);

  const filteredReports = useMemo(() => {
    const sorted = sortAlpha
      ? [...savedReports].sort((a, b) =>
          displayNameOf(a).localeCompare(displayNameOf(b))
        )
      : [...savedReports].sort(
          (a, b) =>
            new Date(b.updated).getTime() - new Date(a.updated).getTime()
        );
    if (!filterText.trim()) return sorted;
    const lower = filterText.toLowerCase();
    return sorted.filter((r) => matchesSearch(r, lower, duplications));
  }, [savedReports, filterText, sortAlpha, duplications]);

  const renderDuplicates = (parentInternalName, isLastParent) => {
    const entries = duplicationsOf(parentInternalName, duplications);
    return entries.map((entry, index) => (
      <SourceRow
        key={`duplicate:${entry.internalName}`}
        prefix={getDuplicateSourcePrefix()}
        label={entry.displayName}
        tooltip={entry.displayName}
        selected={selectedReportSet.has(entry.internalName)}
        onToggle={() => handleToggleReport(entry.internalName)}
        onPreview={() => onPreviewDuplication(entry)}
        onEdit={() => onEditDuplication(entry)}
        isLast={isLastParent && index === entries.length - 1}
      />
    ));
  };

  const handleToggle = useCallback(
    (internalName) => {
      const next = new Set(selectedSet);
      if (next.has(internalName)) {
        next.delete(internalName);
      } else {
        next.add(internalName);
      }
      onSelectedTablesChange([...next]);
    },
    [selectedSet, onSelectedTablesChange]
  );

  const handleToggleReport = useCallback(
    (internalName) => {
      const next = new Set(selectedReportSet);
      if (next.has(internalName)) {
        next.delete(internalName);
      } else {
        next.add(internalName);
      }
      onSelectedReportsChange([...next]);
    },
    [selectedReportSet, onSelectedReportsChange]
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      <TextField
        size={"small"}
        placeholder={translateLabels(labels.REPORT_BUILDER_SEARCH_TABLES, {
          default: "Search tables...",
        })}
        value={filterText}
        onChange={(e) => setFilterText(e.target.value)}
        fullWidth
      />
      <Box
        sx={{
          maxHeight: 500,
          overflowY: "auto",
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1,
        }}
      >
        <SectionDivider
          label={translateLabels(labels.REPORT_BUILDER_TABLES_DIVIDER, {
            default: "— Tables —",
          })}
        />
        {filteredTables.map((table) => (
          <React.Fragment key={table.internalName}>
            <SourceRow
              label={displayNameOf(table)}
              description={table.description}
              selected={selectedSet.has(table.internalName)}
              onToggle={() => handleToggle(table.internalName)}
              onPreview={() =>
                onPreviewTable(table.internalName, displayNameOf(table))
              }
              onDuplicate={() =>
                onDuplicate({
                  kind: "table",
                  internalName: table.internalName,
                  displayName: displayNameOf(table),
                })
              }
            />
            {renderDuplicates(table.internalName, false)}
          </React.Fragment>
        ))}
        {filteredTables.length === 0 && (
          <Typography
            variant={"body2"}
            color={"text.secondary"}
            sx={{ textAlign: "center", py: 2 }}
          >
            {translateLabels(labels.REPORT_BUILDER_NO_TABLES, {
              default: "No tables found",
            })}
          </Typography>
        )}
        <SectionDivider
          label={translateLabels(labels.REPORT_BUILDER_REPORTS_DIVIDER, {
            default: "— Reports —",
          })}
        />
        {filteredReports.map((report, index) => {
          const isLastParent = index === filteredReports.length - 1;
          const hasDuplicates =
            duplicationsOf(report.internalName, duplications).length > 0;
          return (
            <React.Fragment key={report.internalName}>
              <SourceRow
                label={displayNameOf(report)}
                description={report.description}
                selected={selectedReportSet.has(report.internalName)}
                onToggle={() => handleToggleReport(report.internalName)}
                onPreview={() =>
                  onPreviewReport(report.internalName, displayNameOf(report))
                }
                onDuplicate={() =>
                  onDuplicate({
                    kind: "report",
                    internalName: report.internalName,
                    displayName: displayNameOf(report),
                  })
                }
                isLast={isLastParent && !hasDuplicates}
              />
              {renderDuplicates(report.internalName, isLastParent)}
            </React.Fragment>
          );
        })}
        {filteredReports.length === 0 && (
          <Typography
            variant={"body2"}
            color={"text.secondary"}
            sx={{ textAlign: "center", py: 2 }}
          >
            {translateLabels(labels.REPORT_BUILDER_NO_REPORTS, {
              default: "No reports found",
            })}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default TableSelector;
